import SecureImage from "components/SecuredImage";
import React, { useEffect, useRef, useState } from "react";
import { $isImageNode, ImagePayload } from "../ImageNode";
import './ImageEditor.scss';
import { $getNodeByKey, CLICK_COMMAND, COMMAND_PRIORITY_LOW, NodeKey } from "lexical";
import { useLexicalNodeSelection } from "@lexical/react/useLexicalNodeSelection";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Modal } from "react-bootstrap";
import ButtonComponent from "components/ButtonComponent";
import { FaArrowsAltH, FaArrowsAltV, FaLink } from "react-icons/fa";
import LinkInsertModal from "./LinkInsertModal";

type ImageEditorProps = {
  imagePayload: ImagePayload,
  nodeKey: NodeKey
};
const ImageEditor = ({ imagePayload, nodeKey }: ImageEditorProps) => {

  const [editor] = useLexicalComposerContext();
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const [showResizeModal, setShowResizeModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showLinkModal, setShowLinkModal] = useState<boolean>(false);
  const [widthInput, setWidthInput] = useState<string>(imagePayload.width ?? '');
  const [heightInput, setHeightInput] = useState<string>(imagePayload.height ?? '');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => editor.registerCommand(CLICK_COMMAND, (e: MouseEvent) => {
    if (wrapperRef.current?.contains(e.target as Node)) {
      clearSelection();
      setSelected(true);
      return true;
    }
    return false;
  }, COMMAND_PRIORITY_LOW), [editor, setSelected, clearSelection]);

  const applyResize = () => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if ($isImageNode(node)) node.resize(widthInput, heightInput);
    });
    setShowResizeModal(false);
  }

  const deleteImage = () => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if ($isImageNode(node)) node.remove();
    });
    setShowDeleteModal(false);
  }

  const ImageToolbar = <div className='ImageEditor__toolbar'>
    <button
      type='button'
      className='ImageEditor__toolbar__button--left ImageEditor__toolbar__button'
      onClick={() => setShowDeleteModal(true)}>
      X
    </button>
    <button
      type='button'
      className='ImageEditor__toolbar__button'
      onClick={() => {
        setWidthInput(imagePayload.width ?? '');
        setHeightInput(imagePayload.height ?? '');
        setShowResizeModal(true);
      }}>
      Rozmiar
    </button>
    <button
      type='button'
      className='ImageEditor__toolbar__button'
      onClick={() => setShowLinkModal(true)}>
      <FaLink/>
    </button>
  </div>

  const ResizeModal = <Modal show={showResizeModal} onHide={() => setShowResizeModal(false)} centered>
    <Modal.Header closeButton><Modal.Title>Zmień rozmiar obrazka</Modal.Title></Modal.Header>
    <Modal.Body>
      <div className='ImageEditor__modal--resize'>
        <FaArrowsAltV/>
        <input
          id='a'
          className='ImageEditor__modal--resize__input'
          placeholder="Wysokość"
          value={heightInput}
          onChange={(e) => setHeightInput(e.target.value)}
        />
        x
        <input
          className='ImageEditor__modal--resize__input'
          placeholder="Szerokość"
          value={widthInput}
          onChange={(e) => setWidthInput(e.target.value)}
        />
        <FaArrowsAltH/>
      </div>
      <ButtonComponent text='OK' onClick={applyResize} />
      <ButtonComponent text='Anuluj' onClick={() => setShowResizeModal(false)} />
    </Modal.Body>
  </Modal>

  const DeleteModal = <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
    <Modal.Header closeButton><Modal.Title>Usuń obrazek</Modal.Title></Modal.Header>
    <Modal.Body>
      <div className='ImageEditor__modal--delete'>
        Czy napewno chcesz usunąć obrazek?
      </div>
      <ButtonComponent text='Tak' onClick={deleteImage} />
      <ButtonComponent text='Nie' onClick={() => setShowDeleteModal(false)} />
    </Modal.Body>
  </Modal>

  const LinkModal = <LinkInsertModal
    show = {showLinkModal}
    onClose = {() => setShowLinkModal(false)}
    />

  return (
    <div ref={wrapperRef} className={`ImageEditor ${isSelected ? 'ImageEditor--selected' : ''}`}>
      <SecureImage className='ImageEditor__image' src={imagePayload.src} alt={imagePayload.altText} width={imagePayload.width} height={imagePayload.height} />
      {isSelected && ImageToolbar}
      {ResizeModal}
      {DeleteModal}
      {LinkModal}
    </div>
  );
}

export default ImageEditor
